import React from 'react';
import {
    Box,
    Paper,
    Typography,
    IconButton,
    Stack,
    Chip,
    Tooltip,
    LinearProgress,
    Divider,
    Fab,
    Badge,
} from '@mui/material';
import {
    Close as CloseIcon,
    Download as DownloadIcon,
    DeleteOutline,
    CloudDownload as CloudDownloadIcon,
    ErrorOutline,
    HourglassEmpty,
    CheckCircleOutline,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/ar';

import { useExports, labelForType } from '../../context/ExportsContext';

dayjs.extend(relativeTime);

const STATUS_META = {
    pending:   { color: 'warning', Icon: HourglassEmpty,    labelEn: 'Pending',   labelAr: 'قيد التجهيز' },
    completed: { color: 'success', Icon: CheckCircleOutline, labelEn: 'Ready',     labelAr: 'جاهز' },
    failed:    { color: 'error',   Icon: ErrorOutline,      labelEn: 'Failed',    labelAr: 'فشل' },
};

const ExportsTray = () => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const { jobs, trayOpen, setTrayOpen, downloadJob, removeJob, clearCompleted } = useExports();

    const pendingCount = jobs.filter((j) => j.status === 'pending').length;
    const hasJobs = jobs.length > 0;

    // FAB toggle — visible only when there's at least one job to track.
    const fab = hasJobs && (
        <Tooltip title={isArabic ? 'سجل التصدير' : 'Exports'}>
            <Fab
                color="primary"
                size="medium"
                onClick={() => setTrayOpen((prev) => !prev)}
                sx={{
                    position: 'fixed',
                    bottom: 24,
                    insetInlineEnd: 24,
                    zIndex: (t) => t.zIndex.snackbar + 1,
                }}
            >
                <Badge
                    badgeContent={pendingCount || undefined}
                    color="warning"
                    overlap="circular"
                >
                    <CloudDownloadIcon />
                </Badge>
            </Fab>
        </Tooltip>
    );

    if (!hasJobs) return null;

    return (
        <>
            {fab}
            {trayOpen && (
                <Paper
                    elevation={6}
                    sx={{
                        position: 'fixed',
                        bottom: 88,
                        insetInlineEnd: 24,
                        width: { xs: 'calc(100vw - 48px)', sm: 360 },
                        maxHeight: 480,
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 3,
                        overflow: 'hidden',
                        zIndex: (t) => t.zIndex.snackbar,
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ px: 2, py: 1.25, bgcolor: 'primary.main', color: 'primary.contrastText' }}
                    >
                        <Typography variant="subtitle1" fontWeight={700}>
                            {isArabic ? 'سجل التصدير' : 'Exports'}
                        </Typography>
                        <Stack direction="row" spacing={0.5}>
                            <Tooltip title={isArabic ? 'مسح المنجز' : 'Clear finished'}>
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={clearCompleted}
                                        sx={{ color: 'inherit' }}
                                        disabled={jobs.every((j) => j.status === 'pending')}
                                    >
                                        <DeleteOutline fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <IconButton
                                size="small"
                                onClick={() => setTrayOpen(false)}
                                sx={{ color: 'inherit' }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    </Stack>

                    <Box sx={{ overflowY: 'auto', flex: 1 }}>
                        {jobs.map((job, idx) => {
                            const meta = STATUS_META[job.status] || STATUS_META.pending;
                            const Icon = meta.Icon;
                            return (
                                <React.Fragment key={job.id}>
                                    {idx > 0 && <Divider />}
                                    <Box sx={{ p: 1.5 }}>
                                        <Stack
                                            direction="row"
                                            alignItems="flex-start"
                                            spacing={1}
                                        >
                                            <Box
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius: '50%',
                                                    bgcolor: `${meta.color}.lighter`,
                                                    color: `${meta.color}.dark`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <Icon fontSize="small" />
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Stack direction="row" alignItems="center" spacing={0.75}>
                                                    <Typography variant="body2" fontWeight={700} noWrap>
                                                        {labelForType(job.type)}
                                                    </Typography>
                                                    <Chip
                                                        label={isArabic ? meta.labelAr : meta.labelEn}
                                                        size="small"
                                                        color={meta.color}
                                                        variant="tonal"
                                                        sx={{ fontWeight: 'bold', borderRadius: '6px', height: 20 }}
                                                    />
                                                </Stack>
                                                <Typography variant="caption" color="text.secondary">
                                                    {dayjs(job.started_at).locale(isArabic ? 'ar' : 'en').fromNow()}
                                                </Typography>
                                                {job.status === 'pending' && (
                                                    <LinearProgress sx={{ mt: 0.75, borderRadius: 1 }} />
                                                )}
                                                {job.status === 'failed' && job.error && (
                                                    <Typography
                                                        variant="caption"
                                                        color="error"
                                                        sx={{ display: 'block', mt: 0.5 }}
                                                    >
                                                        {job.error}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Stack direction="row" spacing={0.25}>
                                                {job.status === 'completed' && (
                                                    <Tooltip title={isArabic ? 'تنزيل' : 'Download'}>
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => downloadJob(job.id)}
                                                        >
                                                            <DownloadIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {job.status !== 'pending' && (
                                                    <Tooltip title={isArabic ? 'إزالة' : 'Remove'}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => removeJob(job.id)}
                                                        >
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </Stack>
                                    </Box>
                                </React.Fragment>
                            );
                        })}
                    </Box>
                </Paper>
            )}
        </>
    );
};

export default ExportsTray;
